import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Clock, Calendar, Search, Tag, User, ArrowRight } from 'lucide-react'
import { Header } from '@/components/Header'
import { Footer } from '@/components/Footer'
import '@/styles/animations.css'

const categories = [
  'All Articles',
  'Oral Health Tips',
  'Dental Procedures',
  'Preventive Care',
  'Cosmetic Dentistry',
  'Pediatric Dentistry',
  'Emergency Care'
]

const blogPosts = [
  {
    id: 1,
    title: '5 Essential Daily Habits for Better Oral Health',
    excerpt: 'Learn the fundamental daily practices that can significantly improve your oral health and prevent dental problems.',
    content: 'Maintaining excellent oral health doesn\'t require complicated routines. These five simple daily habits can make a dramatic difference in your dental health...',
    category: 'Oral Health Tips',
    author: 'Dr. Aishwarya V',
    date: '2024-01-15',
    readTime: '5 min read',
    tags: ['oral health', 'daily care', 'prevention'],
    featured: true,
    image: 'https://images.unsplash.com/photo-1606811971618-4486d14f3f99?w=400&h=250&fit=crop&auto=format'
  },
  {
    id: 2,
    title: 'Understanding Root Canal Treatment: What to Expect',
    excerpt: 'A comprehensive guide to root canal treatment, addressing common concerns and explaining the procedure step-by-step.',
    content: 'Root canal treatment often causes anxiety, but understanding the process can help ease your concerns. This procedure is designed to save your natural tooth...',
    category: 'Dental Procedures',
    author: 'Dr. Aishwarya V',
    date: '2024-01-10',
    readTime: '8 min read',
    tags: ['root canal', 'endodontics', 'treatment'],
    featured: true,
    image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop&auto=format'
  },
  {
    id: 3,
    title: 'Teeth Whitening: Professional vs At-Home Options',
    excerpt: 'Compare professional teeth whitening treatments with at-home options to make an informed decision about brightening your smile.',
    content: 'A bright, white smile can boost your confidence significantly. But with so many whitening options available, how do you choose the right one?',
    category: 'Cosmetic Dentistry',
    author: 'Dr. Aishwarya V',
    date: '2024-01-05',
    readTime: '6 min read',
    tags: ['teeth whitening', 'cosmetic dentistry', 'smile enhancement'],
    featured: false,
    image: 'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=400&h=250&fit=crop&auto=format'
  },
  {
    id: 4,
    title: 'When to Bring Your Child for Their First Dental Visit',
    excerpt: 'Essential information for parents about when and how to introduce their children to dental care.',
    content: 'Starting dental care early is crucial for your child\'s oral health. Here\'s what every parent should know about pediatric dental care...',
    category: 'Pediatric Dentistry',
    author: 'Dr. Aishwarya V',
    date: '2024-01-01',
    readTime: '4 min read',
    tags: ['pediatric dentistry', 'children', 'first visit'],
    featured: true,
    image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=250&fit=crop&auto=format'
  },
  {
    id: 5,
    title: 'Emergency Dental Care: What Constitutes a Dental Emergency',
    excerpt: 'Learn to identify dental emergencies and know when to seek immediate professional help.',
    content: 'Dental emergencies can be frightening and painful. Knowing what constitutes a true emergency can help you respond appropriately...',
    category: 'Emergency Care',
    author: 'Dr. Aishwarya V',
    date: '2023-12-28',
    readTime: '7 min read',
    tags: ['emergency care', 'dental trauma', 'urgent care'],
    featured: false,
    image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=400&h=250&fit=crop&auto=format'
  },
  {
    id: 6,
    title: 'The Importance of Regular Dental Checkups',
    excerpt: 'Why regular dental visits are crucial for maintaining optimal oral health and preventing serious problems.',
    content: 'Many people only visit the dentist when they have a problem, but regular checkups are essential for preventing dental issues...',
    category: 'Preventive Care',
    author: 'Dr. Aishwarya V',
    date: '2023-12-25',
    readTime: '5 min read',
    tags: ['preventive care', 'checkups', 'oral health'],
    featured: false,
    image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=400&h=250&fit=crop&auto=format'
  },
  {
    id: 7,
    title: 'Gum Disease: Signs, Prevention, and Treatment',
    excerpt: 'Complete guide to understanding gum disease, its warning signs, and effective prevention strategies.',
    content: 'Gum disease is one of the most common dental problems, yet it\'s largely preventable with proper care and early intervention...',
    category: 'Oral Health Tips',
    author: 'Dr. Aishwarya V',
    date: '2023-12-20',
    readTime: '9 min read',
    tags: ['gum disease', 'periodontal care', 'prevention'],
    featured: false,
    image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=400&h=250&fit=crop&auto=format'
  },
  {
    id: 8,
    title: 'Dental Implants: A Complete Guide',
    excerpt: 'Everything you need to know about dental implants, from candidacy to aftercare.',
    content: 'Dental implants offer a permanent solution for missing teeth. Here\'s a comprehensive guide to help you understand the process...',
    category: 'Dental Procedures',
    author: 'Dr. Aishwarya V',
    date: '2023-12-15',
    readTime: '10 min read',
    tags: ['dental implants', 'restoration', 'missing teeth'],
    featured: false,
    image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=400&h=250&fit=crop&auto=format'
  }
]

export function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState('All Articles')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedPost, setSelectedPost] = useState<typeof blogPosts[0] | null>(null)

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All Articles' || post.category === selectedCategory
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    return matchesCategory && matchesSearch
  })

  const featuredPosts = blogPosts.filter(post => post.featured)

  if (selectedPost) {
    return (
      <div className="min-h-screen">
        <Header currentPage="blog" />

        {/* Article Content */}
        <article className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <Button
                variant="ghost"
                onClick={() => setSelectedPost(null)}
                className="mb-4"
              >
                ← Back to Blog
              </Button>
              <div className="flex items-center text-sm text-gray-500 mb-4">
                <Tag className="h-4 w-4 mr-1" />
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full mr-2">{selectedPost.category}</span>
                <User className="h-4 w-4 mr-1" />
                <span className="mr-4">{selectedPost.author}</span>
                <Calendar className="h-4 w-4 mr-1" />
                <span className="mr-4">{new Date(selectedPost.date).toLocaleDateString()}</span>
                <Clock className="h-4 w-4 mr-1" />
                <span>{selectedPost.readTime}</span>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">{selectedPost.title}</h1>
              <p className="text-xl text-gray-600 mb-8">{selectedPost.excerpt}</p>
            </div>

            <div className="prose prose-lg max-w-none">
              <div className="bg-gray-100 rounded-lg h-64 overflow-hidden mb-8">
                <img 
                  src={selectedPost.image} 
                  alt={selectedPost.title}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">Professional Advice</h3>
                <p className="text-blue-800">
                  This article is written by Dr. Aishwarya V, BDS, FAAD (Germany), with years of experience in dental care. 
                  For personalized advice, please schedule a consultation.
                </p>
              </div>

              <div className="text-gray-700 leading-relaxed space-y-6">
                <p>{selectedPost.content}</p>
                
                <p>
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut 
                  labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco 
                  laboris nisi ut aliquip ex ea commodo consequat.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Key Takeaways</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>Regular dental checkups are essential for maintaining oral health</li>
                  <li>Early intervention can prevent more serious dental problems</li>
                  <li>Professional advice is always recommended for specific concerns</li>
                  <li>Maintaining good oral hygiene at home is crucial</li>
                </ul>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mt-8">
                  <h4 className="font-semibold text-yellow-800 mb-2">Have Questions?</h4>
                  <p className="text-yellow-700 mb-4">
                    If you have specific questions about this topic or need personalized dental care, 
                    Dr. Aishwarya is here to help.
                  </p>
                  <Button asChild>
                    <Link to="/book-appointment">Schedule a Consultation</Link>
                  </Button>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t">
                <div className="flex flex-wrap gap-2">
                  {selectedPost.tags.map(tag => (
                    <span key={tag} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <Header currentPage="blog" />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/10 via-yellow-500/8 to-yellow-600/5 animate-pulse"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-20 h-20 bg-gradient-to-br from-yellow-400/20 to-yellow-600/15 rounded-full blur-xl animate-bounce" style={{animationDelay: '0s'}}></div>
          <div className="absolute top-1/2 right-20 w-16 h-16 bg-gradient-to-br from-yellow-500/15 to-yellow-700/10 rounded-full blur-lg animate-bounce" style={{animationDelay: '2s'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 animate-fade-in-up" style={{animationDelay: '0.2s', animationFillMode: 'both'}}>
              Dental Health <span className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent">Education</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto animate-fade-in-up" style={{animationDelay: '0.4s', animationFillMode: 'both'}}>
              Stay informed about dental health with expert advice, tips, and educational content from Dr. Aishwarya V
            </p>
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Featured Articles</h2>
            <p className="text-xl text-gray-600">Essential dental health information for everyone</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredPosts.map((post) => (
              <article key={post.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full mr-2">{post.category}</span>
                    <span>{post.readTime}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{post.title}</h3>
                  <p className="text-gray-600 mb-4">{post.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-500">
                      <User className="h-4 w-4 mr-1" />
                      <span>{post.author}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedPost(post)}
                    >
                      Read More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section id="blog" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 md:mb-0">All Articles</h2>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Categories */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <article key={post.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <div className="h-40 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full mr-2">{post.category}</span>
                    <span>{post.readTime}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{post.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm">{post.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{new Date(post.date).toLocaleDateString()}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedPost(post)}
                    >
                      Read More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>


      <Footer />
    </div>
  )
}